import { AfterViewInit, Component, Input, ViewChild, ElementRef, Output, EventEmitter, ChangeDetectorRef, ViewContainerRef, ComponentRef, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MarkerDrawerComponent } from './marker-drawer/marker-drawer.component';
import { 
  GeoManagerService
} from './geo-manager.service';
import { GeofenceUIBuilderService } from './geofence-ui-builder.service';
import { MapService } from './map.service';
import * as L from 'leaflet';
import { MatSnackBar } from '@angular/material/snack-bar';
import 'leaflet-editable';
import 'leaflet.path.drag';
import { MarkerGroup, MapPolyline, SelectionAction, GeoManagerCaptions, MarkerData, ProcessedMarkerGroup, SelectedMarker, GeofenceData, GeofenceAction, GeofenceMarkersEvent, EditableLayer } from './geo-manager.interfaces';

export type MarkerDisplayMode = 'tooltip' | 'drawer';

@Component({
  selector: 'app-geo-manager',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [GeoManagerService, GeofenceUIBuilderService, MapService],
  templateUrl: './geo-manager.component.html',
  styleUrl: './geo-manager.component.scss',
})
export class GeoManagerComponent implements AfterViewInit, OnDestroy, OnInit, OnChanges {

  @Input() containerId: string = 'map';
  @Input() center: [number, number] = [51.505, -0.09];
  @Input() zoom: number = 13;
  @Input() markerGroups: MarkerGroup[] = [];
  /**
   * Trazos a dibujar sobre el mapa (p. ej. el recorrido real de una ruta).
   *
   * Aditivo: las pantallas que no lo envían se comportan igual que antes.
   * Los puntos van en `[lat, lon]`, el orden de Leaflet — quien traiga GeoJSON
   * (`[lon, lat]`) debe invertirlos antes.
   */
  @Input() polylines: MapPolyline[] = [];
  @Input() displayMode: MarkerDisplayMode = 'tooltip';
  @Input() enableSelectionMode: boolean = false;
  @Input() selectionActions: SelectionAction[] = [];
  @Input() autoSelectFirstMarker: boolean = false;
  @Input() preventDismissDetails: boolean = false;
  @Input() disableMarkerDetails: boolean = false;
  
  // Geofence related inputs
  @Input() enableGeofenceMode: boolean = false;
  @Input() geofences: GeofenceData[] = [];
  @Input() geofenceActions: GeofenceAction[] = [];
  @Input() enableGeofenceEditing: boolean = true;
  
  // Internal geofences array for component state management
  private internalGeofences: GeofenceData[] = [];
  
  private _captions: GeoManagerCaptions = {};
  
  @Input() 
  set captions(value: GeoManagerCaptions) {
    this._captions = this.geoService.mergeCaptions(value);
  }
  
  get captions(): GeoManagerCaptions {
    return this._captions;
  }
  
  @Output() markerSelected = new EventEmitter<{ marker: MarkerData, group: ProcessedMarkerGroup }>();
  @Output() geofenceCreated = new EventEmitter<GeofenceData>();
  @Output() geofenceUpdated = new EventEmitter<GeofenceData>();
  @Output() geofenceDeleted = new EventEmitter<GeofenceData>();
  @Output() geofencesChanged = new EventEmitter<GeofenceData[]>();
  @Output() geofenceMarkersCalculated = new EventEmitter<GeofenceMarkersEvent>();
  
  map!: L.Map;
  processedGroups: ProcessedMarkerGroup[] = [];
  
  // Dynamic drawer management
  private drawerComponentRef: ComponentRef<MarkerDrawerComponent> | null = null;
  private isAnimating = false;

  // Selection mode management
  private selectedMarkers: SelectedMarker[] = [];
  private isActionDropdownOpen: boolean = false;
  private isExecutingAction: boolean = false;
  
  // Internal selection mode state
  public isSelectionModeActive: boolean = false;
  
  // Internal geofence interaction mode state
  public isGeofenceModeActive: boolean = false;

  // Geofence management
  private geofenceDrawingMode: boolean = false;
  private currentDrawingTool: 'polygon' | 'rectangle' | 'circle' | null = null;
  private drawnGeofences: L.Layer[] = [];
  private geofenceLayerGroup!: L.LayerGroup;
  private editableMap: any; // Leaflet.Editable map instance
  private selectedGeofences: GeofenceData[] = [];
  private isGeofenceDrawerOpen: boolean = false;
  private editingGeofenceId: string | null = null;
  private currentDrawingGeofence: EditableLayer | null = null; // Store the currently drawn geofence
  editingGeofence: GeofenceData | null = null; // Made public for template access
  isGeofenceEditMode: boolean = false; // Made public for template access
  isGeofenceCreateMode: boolean = false; // Made public for template access
  private creatingGeofenceType: 'polygon' | 'rectangle' | 'circle' | null = null;
  private originalGeofenceState: GeofenceData | null = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private geoService: GeoManagerService,
    private snackBar: MatSnackBar,
    private geofenceUIBuilder: GeofenceUIBuilderService,
    private mapService: MapService
  ) {}

  // Template references
  @ViewChild('markerTemplate', { static: true }) markerTemplate!: ElementRef;
  @ViewChild('markerBody', { static: true }) markerBody!: ElementRef;
  @ViewChild('markerDot', { static: true }) markerDot!: ElementRef;
  @ViewChild('popupTemplate', { static: true }) popupTemplate!: ElementRef;
  @ViewChild('popupTitle', { static: true }) popupTitle!: ElementRef;
  @ViewChild('popupColor', { static: true }) popupColor!: ElementRef;
  @ViewChild('popupGroupName', { static: true }) popupGroupName!: ElementRef;
  @ViewChild('popupCoords', { static: true }) popupCoords!: ElementRef;
  @ViewChild('selectionDrawerTemplate', { static: true }) selectionDrawerTemplate!: ElementRef;
  @ViewChild('selectionMarkerTemplate', { static: true }) selectionMarkerTemplate!: ElementRef;
  @ViewChild('geofenceDrawerTemplate', { static: true }) geofenceDrawerTemplate!: ElementRef;
  @ViewChild('geofenceItemTemplate', { static: true }) geofenceItemTemplate!: ElementRef;
  @ViewChild('geofenceCreateFormTemplate', { static: true }) geofenceCreateFormTemplate!: ElementRef;
  @ViewChild('geofenceDrawingModeTemplate', { static: true }) geofenceDrawingModeTemplate!: ElementRef;
  
  // Dynamic drawer container
  @ViewChild('drawerContainer', { read: ViewContainerRef, static: true }) drawerContainer!: ViewContainerRef;
  @ViewChild('selectionDrawerContainer', { read: ViewContainerRef, static: true }) selectionDrawerContainer!: ViewContainerRef;
  @ViewChild('geofenceDrawerContainer', { read: ViewContainerRef, static: true }) geofenceDrawerContainer!: ViewContainerRef;

  ngOnInit(): void {
    // Initialize captions with defaults if not provided
    if (!this._captions || Object.keys(this._captions).length === 0) {
      this._captions = this.geoService.mergeCaptions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['markerGroups'] || changes['polylines']) && this.map) {
      this.processMarkerGroups();
      this.addMarkers();
    }
    
    if (changes['geofences'] && this.map) {
      this.processGeofencesInput();
    }
  }

  ngAfterViewInit(): void {
    this.geoService.logInitialization({
      displayMode: this.displayMode,
      enableSelectionMode: this.enableSelectionMode,
      disableMarkerDetails: this.disableMarkerDetails,
      autoSelectFirstMarker: this.autoSelectFirstMarker,
      preventDismissDetails: this.preventDismissDetails,
      markerGroupsCount: this.markerGroups.length
    });
    
    this.initializeMap();
    
    if (this.enableGeofenceMode) {
      // In geofence mode, process markers first, then auto-open the geofence drawer
      this.processMarkerGroups();
      this.addMarkers();
      
      // Set geofence mode as active and open the drawer
      this.isGeofenceModeActive = true;
      setTimeout(() => {
        this.openGeofenceDrawer();
      }, 100);
    } else {
      // Normal marker mode
      this.processMarkerGroups();
      this.addMarkers();
      
      // Auto-select first marker if enabled
      if (this.autoSelectFirstMarker && this.processedGroups.length > 0) {
        const firstGroup = this.processedGroups[0];
        if (firstGroup.markers.length > 0) {
          const firstMarker = firstGroup.markers[0];
          // Use setTimeout to ensure map is fully initialized
          setTimeout(() => {
            this.openDrawer(firstMarker, firstGroup);
          }, 100);
        }
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up any existing drawer component
    this.destroyDrawer();
    
    // Clean up selection drawer
    this.closeSelectionDrawer();
    
    // Clean up geofence drawer
    this.closeGeofenceDrawer();
    
    // Clear all highlights
    this.clearAllHighlights();
    
    // Remove document click listener
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
    
    // Clean up geofence listeners
    if (this.map) {
      this.map.off('editable:drawing:commit');
    }
    
    // Final cleanup
    this.geoService.cleanupContainer(this.drawerContainer);
    this.geoService.cleanupContainer(this.selectionDrawerContainer);
    this.geoService.cleanupContainer(this.geofenceDrawerContainer);
    
    console.log('GeoManager component destroyed and cleaned up');
  }

  private processMarkerGroups(): void {
    this.processedGroups = this.geoService.processMarkerGroups(this.markerGroups);
  }

  private processGeofencesInput(): void {
    if (!this.enableGeofenceMode) return;

    // Clear existing geofences
    this.geofenceLayerGroup.clearLayers();
    this.drawnGeofences = [];

    // Update internal geofences from input (initial load or external changes)
    this.internalGeofences = [...this.geofences];

    // Load new geofences from input
    this.loadGeofences();
    
    // Update drawer content if open - but avoid infinite loops
    if (this.isGeofenceDrawerOpen && !this.isGeofenceEditMode) {
      // Use setTimeout to prevent immediate re-triggering of change detection
      setTimeout(() => {
        this.updateGeofenceDrawerContent();
      }, 0);
    }
  }

  private initializeMap(): void {
    // Create map with editable option if in geofence mode
    if (this.enableGeofenceMode) {
      this.map = this.geoService.createEditableMap(this.containerId, this.center, this.zoom);
      this.editableMap = (this.map as any).editTools;
    } else {
      this.map = this.geoService.createMap(this.containerId, this.center, this.zoom);
    }
    
    // Create geofence layer group if in geofence mode
    if (this.enableGeofenceMode) {
      this.geofenceLayerGroup = L.layerGroup().addTo(this.map);
      
      // Initialize internal geofences from input on first load
      if (this.geofences && this.geofences.length > 0) {
        this.internalGeofences = [...this.geofences];
      }
      
      // Load existing geofences
      this.loadGeofences();
    }
  }

  /**
   * Última nube de puntos encuadrada (paradas + trazos), para poder
   * re-encuadrar a pedido con `fitAll`.
   */
  private lastFitPositions: [number, number][] = [];

  /**
   * Reajusta el mapa a su contenedor. Llamar después de redimensionarlo:
   * Leaflet no detecta cambios de tamaño del contenedor por sí solo y deja
   * tiles grises en la zona nueva.
   */
  resize(): void {
    this.map?.invalidateSize();
  }

  /** Vuelve a encuadrar todas las paradas y trazos, tras navegar o redimensionar. */
  fitAll(): void {
    if (this.map && this.lastFitPositions.length) {
      this.geoService.fitMapToMarkers(this.map, this.lastFitPositions);
    }
  }

  private addMarkers(): void {
    const allMarkerPositions: [number, number][] = [];

    this.processedGroups.forEach(group => {
      group.markers.forEach((marker, index) => {
        const position: [number, number] = [marker.latitude, marker.longitude];
        allMarkerPositions.push(position);
        this.createCustomDivMarker(
          position,
          group,
          marker,
          `${group.groupName} - ${marker.title}`
        );
      });
    });

    // Los trazos entran al encuadre: una ruta puede rodear por fuera de sus
    // paradas, y encuadrar solo los pines la dejaría cortada en pantalla.
    allMarkerPositions.push(...this.addPolylines());

    // Auto-fit map to show all markers if we have any
    this.lastFitPositions = allMarkerPositions;
    if (allMarkerPositions.length > 0) {
      this.geoService.fitMapToMarkers(this.map, allMarkerPositions);
    }
  }

  /** Dibuja los trazos recibidos y devuelve sus puntos, para el encuadre. */
  private addPolylines(): [number, number][] {
    const positions: [number, number][] = [];
    for (const line of this.polylines ?? []) {
      if (!line?.points?.length) continue;
      L.polyline(line.points, {
        color: line.color ?? '#6366f1',
        weight: line.weight ?? 4,
        opacity: line.opacity ?? 0.75,
      }).addTo(this.map);
      positions.push(...line.points);
    }
    return positions;
  }

  private createCustomDivMarker(position: [number, number], group: ProcessedMarkerGroup, marker: MarkerData, popupText: string): void {
    // Create marker icon using template
    const markerElement = this.geoService.createMarkerElement(this.markerTemplate.nativeElement, group, marker);
    const customIcon = L.divIcon({
      className: 'custom-div-marker',
      html: markerElement,
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [0, -40]
    });

    // Create marker
    const leafletMarker = L.marker(position, { icon: customIcon }).addTo(this.map);
    
    // Add click handler based on display mode - always allow marker details regardless of geofence mode
    if (this.displayMode === 'tooltip') {
      // Add click handler that always emits event
      leafletMarker.on('click', () => {
        if (!this.isSelectionModeActive) {
          this.markerSelected.emit({ marker, group });
          
          // Log to console when details are disabled
          if (this.disableMarkerDetails) {
            this.geoService.logMarkerClick(marker, group);
          }
        }
      });
      
      // Only bind popup if details are not disabled
      if (!this.disableMarkerDetails) {
        // Create popup content using template
        const popupElement = this.geoService.createPopupElement(this.popupTemplate.nativeElement, group, marker, position);
        leafletMarker.bindPopup(popupElement);
      }
    } else if (this.displayMode === 'drawer') {
      console.log('Drawer mode enabled, rendering marker with drawer support');
      // Add click handler for drawer mode
      leafletMarker.on('click', () => {
        if (this.isSelectionModeActive) {
          this.toggleMarkerSelection(marker, group);
        } else {
          // Always emit the event
          this.markerSelected.emit({ marker, group });
          
          // Log to console when details are disabled
          if (this.disableMarkerDetails) {
            this.geoService.logMarkerClick(marker, group);
          }
          
          // Only show details if not disabled
          if (!this.disableMarkerDetails) {
            this.openDrawer(marker, group);
          }
        }
      });
    }
  }

  reloadMap(){
    this.initializeMap();
    this.processMarkerGroups();
    this.addMarkers()
  }

  showMarkerDetails(latitude: number, longitude: number): void {
    if (!this.map) return;

    // Centrar el mapa en las coordenadas
    const mapSize = this.map.getSize();
    const offsetX = -mapSize.x * 0.15; // ajusta según el ancho del drawer
    const offsetY = 0;

    this.map.panTo([latitude, longitude], { animate: false });
    this.map.panBy([offsetX, offsetY], { animate: true, duration: 0.5 });

    // Buscar el marker que coincida con las coordenadas
    this.processedGroups.forEach(group => {
      group.markers.forEach(marker => {
        if (marker.latitude === latitude && marker.longitude === longitude) {
          if (this.displayMode === 'tooltip') {
            this.map.eachLayer((layer: any) => {
              if (layer instanceof L.Marker) {
                const pos = layer.getLatLng();
                if (pos.lat === latitude && pos.lng === longitude) {
                  layer.openPopup();
                }
              }
            });
          } else {
            this.openDrawer(marker, group);
          }
        }
      });
    });
  }

  openDrawer(marker: MarkerData, group: ProcessedMarkerGroup): void {
    console.log('Opening drawer for marker:', marker.title);
    
    // Prevent multiple operations while animating
    if (this.isAnimating) {
      console.log('Drawer is animating, ignoring click');
      return;
    }
    
    // If a drawer is already open, destroy and recreate without animation
    if (this.drawerComponentRef) {
      console.log('Existing drawer found, destroying and recreating without animation');
      this.destroyDrawer();
      this.createAndOpenDrawer(marker, group, false); // false = no opening animation
      return;
    }
    
    // No existing drawer, ensure container is clean and create new one
    this.geoService.cleanupContainer(this.drawerContainer);
    this.createAndOpenDrawer(marker, group, true); // true = with opening animation
  }

  private async createAndOpenDrawer(marker: MarkerData, group: ProcessedMarkerGroup, withAnimation: boolean = true): Promise<void> {
    console.log('Creating and opening drawer for:', marker.title, withAnimation ? 'with animation' : 'without animation');
    this.isAnimating = withAnimation;
    
    // Create the drawer component using the service
    this.drawerComponentRef = this.geoService.createDrawerComponent(
      this.drawerContainer,
      marker,
      group,
      this.captions,
      this.preventDismissDetails,
      () => this.closeDrawer()
    );
    
    if (withAnimation) {
      // Animate open
      await this.geoService.animateDrawerOpen(this.drawerComponentRef);
      this.isAnimating = false;
    } else {
      // Open immediately
      this.drawerComponentRef.instance.isOpen = true;
      this.drawerComponentRef.changeDetectorRef.detectChanges();
      console.log('Drawer opened immediately without animation');
    }
  }

  // Selection mode toggle method
  public toggleSelectionMode(): void {
    // Prevent action if geofence mode is active
    if (this.isGeofenceModeActive) {
      console.log('Cannot activate selection mode: geofence mode is currently active');
      return;
    }

    if (!this.enableSelectionMode || this.selectionActions.length === 0) {
      console.log('Selection mode is not enabled or no actions available');
      return;
    }

    this.isSelectionModeActive = !this.isSelectionModeActive;
    console.log('Selection mode toggled:', this.isSelectionModeActive ? 'ON' : 'OFF');

    if (this.isSelectionModeActive) {
      // Selection mode turned ON - close details drawer and open selection drawer
      if (this.drawerComponentRef) {
        console.log('Selection mode activated: closing details drawer');
        this.destroyDrawer();
      }
      console.log('Selection mode activated: opening selection drawer');
      this.openSelectionDrawer();
    } else {
      // Selection mode turned OFF - clear selections and close selection drawer
      console.log('Selection mode deactivated: clearing selections and closing selection drawer');
      this.clearAllSelections();
      this.closeSelectionDrawer();
    }
  }

  // Selection mode methods
  toggleMarkerSelection(marker: MarkerData, group: ProcessedMarkerGroup): void {
    const existingIndex = this.selectedMarkers.findIndex(
      selected => selected.marker === marker && selected.group === group
    );

    if (existingIndex >= 0) {
      // Deselect marker
      this.selectedMarkers.splice(existingIndex, 1);
      console.log('Marker deselected:', marker.title);
    } else {
      // Select marker
      this.selectedMarkers.push({ marker, group });
      console.log('Marker selected:', marker.title);
    }

    // Always update the drawer content when selections change
    this.updateSelectionDrawerContent();
  }

  private removeMarkerFromSelection(marker: MarkerData, group: ProcessedMarkerGroup): void {
    const existingIndex = this.selectedMarkers.findIndex(
      selected => selected.marker === marker && selected.group === group
    );

    if (existingIndex >= 0) {
      this.selectedMarkers.splice(existingIndex, 1);
      console.log('Marker removed from selection:', marker.title);
      this.updateSelectionDrawerContent();
    }
  }

  private openSelectionDrawer(): void {
    console.log('Opening selection drawer');
    
    // First ensure any existing drawer is completely removed
    this.closeSelectionDrawer();
    
    // Now create the new drawer
    this.createSimpleSelectionDrawer();
  }

  private createSimpleSelectionDrawer(): void {
    // Check if container is available
    if (!this.selectionDrawerContainer) {
      this.snackBar.open('Selection drawer container not available',"Cerrar",{ duration: 5000});
      return;
    }
    
    // Ensure container is completely clean before creating new drawer
    this.selectionDrawerContainer.clear();
    const containerElement = this.selectionDrawerContainer.element.nativeElement;
    
    // Force remove any lingering elements
    if (containerElement) {
      while (containerElement.firstChild) {
        console.log('Removing lingering selection drawer element:', containerElement.firstChild);
        containerElement.removeChild(containerElement.firstChild);
      }
    }
    
    // Clone the selection drawer template
    const drawerElement = this.selectionDrawerTemplate.nativeElement.cloneNode(true) as HTMLElement;
    
    // Populate action items in the dropdown
    this.populateActionItems(drawerElement);
    
    // Append to the container
    containerElement.appendChild(drawerElement);
    
    // Initialize the drawer content
    this.updateSelectionDrawerContent();
    
    // Add event listeners
    this.addSelectionDrawerEventListeners(drawerElement);

    console.log('Selection drawer created from template and added to DOM');
  }

  private populateActionItems(drawerElement: HTMLElement): void {
    const actionsDropdown = drawerElement.querySelector('.actions-dropdown .py-1');
    if (!actionsDropdown || this.selectionActions.length === 0) return;

    // Clear any existing action items
    actionsDropdown.innerHTML = '';

    // Add each action as a button
    this.selectionActions.forEach(action => {
      const actionButton = document.createElement('button');
      actionButton.className = 'action-item w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200';
      actionButton.dataset['actionKey'] = action.key;
      actionButton.textContent = action.caption;
      actionsDropdown.appendChild(actionButton);
    });
  }

  private addSelectionDrawerEventListeners(drawerElement: HTMLElement): void {
    // Add clear all button functionality
    const clearButton = drawerElement.querySelector('.clear-all-selections');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearAllSelections();
      });
    }

    // Add dropdown toggle functionality
    const dropdownToggle = drawerElement.querySelector('.actions-dropdown-toggle');
    if (dropdownToggle) {
      dropdownToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleActionDropdown();
      });
    }

    // Add action item click handlers
    const actionItems = drawerElement.querySelectorAll('.action-item');
    actionItems.forEach(item => {
      item.addEventListener('click', async (e) => {
        e.stopPropagation();
        const actionKey = (item as HTMLElement).dataset['actionKey'];
        if (actionKey && !this.isExecutingAction) {
          await this.executeSelectionAction(actionKey);
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  private updateSelectionDrawerContent(): void {
    const containerElement = this.selectionDrawerContainer?.element.nativeElement;
    if (!containerElement) return;

    const drawerElement = containerElement.querySelector('.selection-drawer-panel');
    if (!drawerElement) return;

    // Update count
    const countElement = drawerElement.querySelector('.selection-count');
    if (countElement) {
      countElement.textContent = `${this.selectedMarkers.length} seleccionados`;
    }

    // Update clear button visibility
    const clearButton = drawerElement.querySelector('.clear-all-selections');
    if (clearButton) {
      if (this.selectedMarkers.length > 0) {
        (clearButton as HTMLElement).style.display = 'inline-block';
      } else {
        (clearButton as HTMLElement).style.display = 'none';
      }
    }

    // Update actions dropdown toggle visibility
    const actionsDropdownToggle = drawerElement.querySelector('.actions-dropdown-toggle');
    if (actionsDropdownToggle) {
      const parentDiv = actionsDropdownToggle.parentElement;
      if (parentDiv && this.selectedMarkers.length > 0 && this.selectionActions.length > 0) {
        (parentDiv as HTMLElement).style.display = 'block';
      } else if (parentDiv) {
        (parentDiv as HTMLElement).style.display = 'none';
      }
    }

    // Update actions dropdown visibility and content
    const actionsDropdown = drawerElement.querySelector('.actions-dropdown');
    if (actionsDropdown) {
      if (this.isActionDropdownOpen) {
        actionsDropdown.classList.remove('hidden');
      } else {
        actionsDropdown.classList.add('hidden');
      }
      
      // Update action items with current execution state
      const actionItems = actionsDropdown.querySelectorAll('.action-item');
      actionItems.forEach(item => {
        const button = item as HTMLButtonElement;
        if (this.isExecutingAction) {
          button.classList.add('opacity-50', 'cursor-not-allowed');
          button.disabled = true;
        } else {
          button.classList.remove('opacity-50', 'cursor-not-allowed');
          button.disabled = false;
        }
      });
    }

    // Update marker list
    const markerListElement = drawerElement.querySelector('.marker-list');
    const emptyStateElement = drawerElement.querySelector('.empty-state');
    
    if (!markerListElement || !emptyStateElement) return;

    if (this.selectedMarkers.length === 0) {
      markerListElement.style.display = 'none';
      emptyStateElement.style.display = 'block';
    } else {
      markerListElement.style.display = 'block';
      emptyStateElement.style.display = 'none';
      
      // Clear existing marker items
      markerListElement.innerHTML = '';
      
      // Add marker items using template
      this.selectedMarkers.forEach(selected => {
        const markerElement = this.createSelectedMarkerElement(selected.marker, selected.group);
        markerListElement.appendChild(markerElement);
      });
    }
  }

  private createSelectedMarkerElement(marker: MarkerData, group: ProcessedMarkerGroup): HTMLElement {
    return this.geoService.createSelectedMarkerElement(
      this.selectionMarkerTemplate.nativeElement,
      marker,
      group,
      this.captions,
      (marker, group) => this.removeMarkerFromSelection(marker, group),
      (marker) => {
        if (marker.latitude && marker.longitude && this.map) {
          this.map.setView([marker.latitude, marker.longitude], this.map.getZoom());
        }
      }
    );
  }

  private closeSelectionDrawer(): void {
    // Remove document click listener when closing drawer
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
    
    // Clear the container and force DOM cleanup for simple HTML approach
    this.geoService.cleanupContainer(this.selectionDrawerContainer);
    console.log('Selection drawer closed and removed from DOM');
  }

  // Action dropdown methods
  private toggleActionDropdown(): void {
    this.isActionDropdownOpen = !this.isActionDropdownOpen;
    this.updateSelectionDrawerContent();
  }

  private handleDocumentClick(event: Event): void {
    // Close dropdown if clicking outside of it
    const target = event.target as HTMLElement;
    if (!target.closest('.actions-dropdown') && !target.closest('.actions-dropdown-toggle')) {
      if (this.isActionDropdownOpen) {
        this.isActionDropdownOpen = false;
        this.updateSelectionDrawerContent();
      }
    }
  }

  private async executeSelectionAction(actionKey: string): Promise<void> {
    const action = this.selectionActions.find(a => a.key === actionKey);
    if (!action) {
      this.snackBar.open(`Action with key "${actionKey}" not found`,"Cerrar",{ duration: 5000});
      return;
    }

    if (this.isExecutingAction) {
      this.snackBar.open('Action already executing, ignoring request',"Cerrar",{ duration: 5000});
      return;
    }

    try {
      this.isExecutingAction = true;
      this.isActionDropdownOpen = false;
      this.updateSelectionDrawerContent();

      console.log(`Executing action: ${action.caption}`);
      const result = await action.handler([...this.selectedMarkers]);
      
      if (result === true) {
        // Action succeeded and wants to close drawer and clear selection
        console.log('Action completed successfully, clearing selections and closing drawer');
        this.clearAllSelections();
        this.toggleSelectionMode(); // This will close the drawer
      } else {
        console.log('Action completed but drawer remains open');
      }
    } catch (error) {
      this.snackBar.open(error.error.message || 'Error executing selection action:',"Cerrar",{ duration: 5000});
    } finally {
      this.isExecutingAction = false;
      // Only update if we're still in selection mode
      if (this.isSelectionModeActive) {
        this.updateSelectionDrawerContent();
      }
    }
  }

  /**
   * Execute edit action for a geofence using the configured geofence actions
   */
  private async executeGeofenceEditAction(geofence: GeofenceData): Promise<boolean> {
    // Look for an edit action in the geofenceActions
    const editAction = this.geofenceActions.find(action => 
      action.key === 'edit' || action.key === 'update' || 
      action.caption.toLowerCase().includes('edit') || 
      action.caption.toLowerCase().includes('update') ||
      action.caption.toLowerCase().includes('editar') ||
      action.caption.toLowerCase().includes('actualizar')
    );

    if (!editAction) {
      console.warn('No edit action found in geofenceActions, falling back to direct update');
      return true; // Allow the update if no specific action is configured
    }

    if (this.isExecutingAction) {
      console.log('Action already executing, ignoring edit request');
      return false;
    }

    try {
      this.isExecutingAction = true;
      console.log(`Executing edit action: ${editAction.caption} for geofence: ${geofence.name}`);
      
      // Call the handler with the updated geofence
      const result = await editAction.handler([geofence]);
      
      if (result === true) {
        console.log('Edit action completed successfully');
        return true;
      } else {
        console.log('Edit action was cancelled or failed');
        return false;
      }
    } catch (error) {
      console.error('Error executing geofence edit action:', error);
      return false;
    } finally {
      this.isExecutingAction = false;
    }
  }

  /**
   * Execute create action for a geofence using the configured geofence actions
   */
  private async executeGeofenceCreateAction(geofence: GeofenceData): Promise<boolean> {
    // Look for a create action in the geofenceActions
    const createAction = this.geofenceActions.find(action => 
      action.key === 'create' || action.key === 'add' || 
      action.caption.toLowerCase().includes('create') || 
      action.caption.toLowerCase().includes('add') ||
      action.caption.toLowerCase().includes('crear') ||
      action.caption.toLowerCase().includes('agregar')
    );

    if (!createAction) {
      console.warn('No create action found in geofenceActions, falling back to direct creation');
      return true; // Allow the creation if no specific action is configured
    }

    if (this.isExecutingAction) {
      console.log('Action already executing, ignoring create request');
      return false;
    }

    try {
      this.isExecutingAction = true;
      console.log(`Executing create action: ${createAction.caption} for geofence: ${geofence.name}`);
      
      // Call the handler with the new geofence
      const result = await createAction.handler([geofence]);
      
      if (result === true) {
        console.log('Create action completed successfully');
        return true;
      } else {
        console.log('Create action was cancelled or failed');
        return false;
      }
    } catch (error) {
      console.error('Error executing geofence create action:', error);
      return false;
    } finally {
      this.isExecutingAction = false;
    }
  }

  private clearAllSelections(): void {
    this.selectedMarkers = [];
    // Only update drawer content if we're in selection mode and drawer exists
    if (this.isSelectionModeActive) {
      this.updateSelectionDrawerContent();
    }
    console.log('All selections cleared');
  }

  // ============= GEOFENCE MANAGEMENT METHODS =============

  private loadGeofences(): void {
    this.internalGeofences.forEach(geofence => {
      this.addGeofenceToMap(geofence);
    });
  }

  private addGeofenceToMap(geofence: GeofenceData): void {
    const layer = this.mapService.addGeofenceToMap(
      geofence, 
      this.geofenceLayerGroup, 
      this.drawnGeofences,
      this.editableMap,
      (geofence) => {
        if (this.enableGeofenceEditing) {
          this.selectGeofence(geofence);
        }
      }
    );
  }

  public toggleGeofenceMode(): void {
    // Prevent action if selection mode is active
    if (this.isSelectionModeActive) {
      console.log('Cannot activate geofence mode: selection mode is currently active');
      return;
    }

    if (!this.enableGeofenceMode) {
      console.log('Geofence mode is not enabled');
      return;
    }

    this.isGeofenceModeActive = !this.isGeofenceModeActive;
    console.log('Geofence mode toggled:', this.isGeofenceModeActive ? 'ON' : 'OFF');

    if (this.isGeofenceModeActive) {
      this.openGeofenceDrawer();
    } else {
      this.closeGeofenceDrawer();
    }
  }

  private openGeofenceDrawer(): void {
    this.isGeofenceDrawerOpen = true;
    this.createGeofenceDrawer();
  }

  private closeGeofenceDrawer(): void {
    this.isGeofenceDrawerOpen = false;
    this.geoService.cleanupContainer(this.geofenceDrawerContainer);
  }

  private createGeofenceDrawer(): void {
    if (!this.geofenceDrawerContainer) {
      console.error('Geofence drawer container not available');
      return;
    }

    this.geoService.cleanupContainer(this.geofenceDrawerContainer);
    const containerElement = this.geofenceDrawerContainer.element.nativeElement;

    const drawerElement = this.geofenceDrawerTemplate.nativeElement.cloneNode(true) as HTMLElement;
    containerElement.appendChild(drawerElement);

    this.updateGeofenceDrawerContent();
    this.addGeofenceDrawerEventListeners(drawerElement);
  }

  private updateGeofenceDrawerContent(): void {
    // Use service to update content, but handle container access directly
    const containerElement = this.geofenceDrawerContainer?.element?.nativeElement;
    if (!containerElement) {
      console.log('No container element found');
      return;
    }

    const drawerElement = containerElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) {
      console.log('No drawer element found');
      return;
    }

    // Check if we need to restore the original drawer structure
    const editFormElement = drawerElement.querySelector('.geofence-edit-form');
    if (editFormElement) {
      console.log('Edit form found, need to restore original drawer structure');
      this.restoreOriginalDrawerStructure(drawerElement);
      return;
    }

    const geofenceListElement: HTMLElement = drawerElement.querySelector('.geofences-list');
    const emptyStateElement: HTMLElement = drawerElement.querySelector('.no-geofences-message');

    if (!geofenceListElement || !emptyStateElement) {
      console.log('List elements not found, need to restore drawer structure');
      this.restoreOriginalDrawerStructure(drawerElement);
      return;
    }

    if (this.internalGeofences.length === 0) {
      this.geoService.toggleElementVisibility(geofenceListElement, false);
      this.geoService.toggleElementVisibility(emptyStateElement, true);
    } else {
      this.geoService.toggleElementVisibility(geofenceListElement, true);
      this.geoService.toggleElementVisibility(emptyStateElement, false);

      geofenceListElement.innerHTML = '';
      this.internalGeofences.forEach(geofence => {
        const geofenceElement = this.createGeofenceElement(geofence);
        geofenceListElement.appendChild(geofenceElement);
      });
    }
    
    console.log('updateGeofenceDrawerContent completed successfully');
  }

  private restoreOriginalDrawerStructure(drawerElement: HTMLElement): void {
    this.geofenceUIBuilder.restoreOriginalDrawerStructure(
      drawerElement,
      this.geofenceDrawerTemplate,
      (drawerElement: HTMLElement) => this.addGeofenceDrawerEventListeners(drawerElement),
      (element: HTMLElement, visible: boolean) => this.geoService.toggleElementVisibility(element, visible)
    );
  }

  private createGeofenceElement(geofence: GeofenceData): HTMLElement {
    return this.geofenceUIBuilder.createGeofenceElement(
      geofence,
      this.geofenceItemTemplate,
      this.processedGroups,
      this.captions,
      (type: string) => this.getGeofenceTypeLabel(type),
      (geofence: GeofenceData) => this.selectGeofence(geofence),
      (geofenceId: string) => this.editGeofence(geofenceId),
      (geofence: GeofenceData) => this.executeGeofenceRemoveAction(geofence),
      (geofence: GeofenceData, markers: MarkerData[]) => this.geoService.calculateMarkersInGeofence(geofence, markers)
    );
  }

  private getGeofenceTypeLabel(type: string): string {
    return this.geofenceUIBuilder.getGeofenceTypeLabel(type);
  }

  private addGeofenceDrawerEventListeners(drawerElement: HTMLElement): void {
    // Add drawing tool handlers
    const polygonBtn = drawerElement.querySelector('.draw-polygon-btn');
    if (polygonBtn) {
      polygonBtn.addEventListener('click', () => this.startDrawing('polygon'));
    }

    const rectangleBtn = drawerElement.querySelector('.draw-rectangle-btn');
    if (rectangleBtn) {
      rectangleBtn.addEventListener('click', () => this.startDrawing('rectangle'));
    }

    const circleBtn = drawerElement.querySelector('.draw-circle-btn');
    if (circleBtn) {
      circleBtn.addEventListener('click', () => this.startDrawing('circle'));
    }
  }

  startDrawing(type: 'polygon' | 'rectangle' | 'circle'): void {
    this.enterDrawingMode(type);
  }

  private enterDrawingMode(type: 'polygon' | 'rectangle' | 'circle'): void {
    // Store the drawing type and current drawing geofence
    this.creatingGeofenceType = type;
    this.currentDrawingGeofence = null;
    
    // Show drawing mode interface
    this.showDrawingModeInterface(type);
    
    // Start the actual drawing tool
    this.startActualDrawing();
  }

  private showDrawingModeInterface(type: 'polygon' | 'rectangle' | 'circle'): void {
    // Convert ViewContainerRef to ElementRef-like object
    const containerElement = { nativeElement: this.geofenceDrawerContainer.element.nativeElement };
    
    this.geofenceUIBuilder.showDrawingModeInterface(
      type,
      containerElement as any,
      this.geofenceDrawingModeTemplate,
      (type: string) => this.getGeofenceTypeLabel(type),
      () => this.cancelDrawingMode(),
      () => this.addEscapeKeyListener()
    );
  }

  private addEscapeKeyListener(): void {
    const escapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.cancelDrawingMode();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  private cancelDrawingMode(): void {
    // Stop any active drawing
    if (this.editableMap && this.currentDrawingTool) {
      this.editableMap.stopDrawing();
      this.map.off('editable:drawing:commit', this.onGeofenceDrawingComplete.bind(this));
    }
    
    // Remove any temporary drawn geofence
    if (this.currentDrawingGeofence) {
      this.currentDrawingGeofence.remove();
      this.currentDrawingGeofence = null;
    }
    
    // Reset state
    this.geofenceDrawingMode = false;
    this.currentDrawingTool = null;
    this.creatingGeofenceType = null;
    this.isGeofenceCreateMode = false;
    
    // Restore full drawer structure
    this.restoreFullDrawerStructure();
  }

  private generateId(): string {
    return this.geofenceUIBuilder.generateId();
  }

  cancelGeofenceAction(): void {
    if (this.isGeofenceCreateMode) {
      this.isGeofenceCreateMode = false;
      this.creatingGeofenceType = null;
      this.editingGeofence = null;
      this.geofenceDrawingMode = false;
      
      // Cancel any active drawing
      if (this.editableMap && this.currentDrawingTool) {
        this.editableMap.stopDrawing();
        this.map.off('editable:drawing:commit', this.onGeofenceDrawingComplete.bind(this));
      }
    } else if (this.isGeofenceEditMode) {
      // For edit mode, restore original state and geometry
      if (this.originalGeofenceState && this.editingGeofenceId) {
        const index = this.internalGeofences.findIndex(g => g.id === this.editingGeofenceId);
        if (index !== -1) {
          this.internalGeofences[index] = { ...this.originalGeofenceState };
          
          // Update the layer with original data and restore geometry
          const layer: EditableLayer = this.drawnGeofences.find(l => 
            (l as EditableLayer).geofenceData?.id === this.editingGeofenceId
          );
          if (layer) {
            layer.geofenceData = this.originalGeofenceState;
            
            // Restore original geometry (shape and position)
            this.restoreLayerGeometry(layer, this.originalGeofenceState);
          }
        }
      }
      
      this.isGeofenceEditMode = false;
      this.editingGeofenceId = null;
      this.editingGeofence = null;
      this.originalGeofenceState = null;
    }
    
    // Clear any highlights when cancelling
    this.clearAllHighlights();
    
    this.currentDrawingTool = null;
  }

  async saveGeofence(): Promise<void> {
    if (!this.editingGeofence) return;

    if (this.isGeofenceCreateMode) {
      // For creation, only save if coordinates exist
      if (!this.editingGeofence.coordinates || this.editingGeofence.coordinates.length === 0) {
        alert('Por favor dibuje la geocerca en el mapa antes de guardar.');
        return;
      }
      
      // Use the action-based pattern for creation
      const createResult = await this.executeGeofenceCreateAction({ ...this.editingGeofence });
      
      if (createResult) {
        // Add to the beginning of the array only if action succeeded
        this.internalGeofences.unshift({ ...this.editingGeofence });
        this.isGeofenceCreateMode = false;
        this.creatingGeofenceType = null;
        console.log('Geofence created successfully:', this.editingGeofence);
        
        // Clean up and emit changes
        this.editingGeofence = null;
        this.originalGeofenceState = null;
        this.currentDrawingTool = null;
        this.geofenceDrawingMode = false;
        
        // Emit the changes and update drawer
        this.geofencesChanged.emit([...this.internalGeofences]);
        this.updateGeofenceDrawerContent();
      } else {
        // Action was cancelled or failed - stay in creation mode
        console.log('Geofence creation was cancelled - staying in creation mode');
        // Don't reset state - keep the form open so user can try again or manually cancel
        return;
      }
    } else if (this.isGeofenceEditMode) {
      // For editing, update existing geofence
      const index = this.internalGeofences.findIndex(g => g.id === this.editingGeofence!.id);
      if (index !== -1) {
        this.internalGeofences[index] = { ...this.editingGeofence };
      }
      this.isGeofenceEditMode = false;
      this.editingGeofenceId = null;
      
      // Clean up
      this.editingGeofence = null;
      this.originalGeofenceState = null;
      this.currentDrawingTool = null;
      this.geofenceDrawingMode = false;
      
      // Emit the changes and update drawer
      this.geofencesChanged.emit([...this.internalGeofences]);
      this.updateGeofenceDrawerContent();
    }
  }

  startActualDrawing(): void {
    if (!this.editableMap || !this.creatingGeofenceType) return;

    this.currentDrawingTool = this.creatingGeofenceType;
    this.geofenceDrawingMode = true;

    switch (this.creatingGeofenceType) {
      case 'polygon':
        this.editableMap.startPolygon();
        break;
      case 'rectangle':
        this.editableMap.startRectangle();
        break;
      case 'circle':
        this.editableMap.startCircle();
        break;
    }

    // Listen for drawing completion
    this.map.on('editable:drawing:commit', this.onGeofenceDrawingComplete.bind(this));
  }

  private onGeofenceDrawingComplete(event: any): void {
    this.geofenceDrawingMode = false;
    this.map.off('editable:drawing:commit', this.onGeofenceDrawingComplete.bind(this));

    const layer = event.layer;
    
    if (this.creatingGeofenceType) {
      // Store the drawn layer
      this.currentDrawingGeofence = layer;
      
      // Make the layer non-editable initially
      layer.disableEdit();
      
      // Add to the map temporarily
      this.geofenceLayerGroup.addLayer(layer);
      
      // Create temporary geofence data
      const tempGeofence: GeofenceData = {
        id: this.generateId(),
        name: `${this.getGeofenceTypeLabel(this.creatingGeofenceType)} ${this.internalGeofences.length + 1}`,
        description: '',
        type: this.creatingGeofenceType,
        coordinates: [],
        color: '#3B82F6'
      };
      
      // Update coordinates from the drawn layer
      this.updateGeofenceFromLayer(tempGeofence, layer);
      
      // Show the creation form with the drawn geofence data
      this.showCreateFormWithDrawnGeofence(tempGeofence);
    } else {
      // No valid creation type - remove the layer and cancel the operation
      console.warn('Drawing completed but no valid geofence type set. Removing layer.');
      if (layer && this.geofenceLayerGroup.hasLayer(layer)) {
        this.geofenceLayerGroup.removeLayer(layer);
      }
    }
  }

  private showCreateFormWithDrawnGeofence(geofence: GeofenceData): void {
    const containerElement = { nativeElement: this.geofenceDrawerContainer.element.nativeElement };
    
    this.geofenceUIBuilder.showCreateFormWithDrawnGeofence(
      geofence,
      containerElement as any,
      this.geofenceCreateFormTemplate,
      (type: string) => this.getGeofenceTypeLabel(type),
      (formTemplate: HTMLElement, geofenceData: GeofenceData, isEditMode: boolean) => 
        this.addGeofenceFormEventListeners(formTemplate, geofenceData, isEditMode)
    );
    
    // Set state
    this.isGeofenceCreateMode = true;
    this.editingGeofence = geofence;
  }

  private addGeofenceFormEventListeners(formElement: HTMLElement, geofence: GeofenceData, isEditMode: boolean = false): void {
    const cancelBtns = formElement.querySelectorAll('.cancel-form-btn');
    cancelBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (isEditMode) {
          this.cancelGeofenceEdit();
        } else {
          this.cancelDrawingMode();
        }
      });
    });

    const saveBtn = formElement.querySelector('.save-geofence-btn');
    if (saveBtn) {
      if (isEditMode) {
        saveBtn.addEventListener('click', () => this.saveGeofenceEdits(geofence));
      } else {
        saveBtn.addEventListener('click', async () => await this.saveDrawnGeofence(formElement, geofence));
      }
    }

    // Color input change handler
    const colorInput = formElement.querySelector('.geofence-color-input') as HTMLInputElement;
    const colorDisplay = formElement.querySelector('.geofence-color-display');
    if (colorInput && colorDisplay) {
      colorInput.addEventListener('change', () => {
        colorDisplay.textContent = colorInput.value;
        // Update the drawn geofence color
        if (isEditMode) {
          this.updateGeofenceColor(geofence.id, colorInput.value);
        } else {
          this.updateDrawnGeofenceColor(colorInput.value);
        }
      });
    }
  }

  private updateDrawnGeofenceColor(color: string): void {
    if (this.currentDrawingGeofence) {
      // Update the layer style
      if (this.currentDrawingGeofence instanceof L.Path) {
        this.currentDrawingGeofence.setStyle({ color: color, fillColor: color });
      }
    }
  }

  private async saveDrawnGeofence(formElement: HTMLElement, geofence: GeofenceData): Promise<void> {
    // Get updated form values
    const nameInput = formElement.querySelector('.geofence-name-input') as HTMLInputElement;
    const descriptionInput = formElement.querySelector('.geofence-description-input') as HTMLTextAreaElement;
    const colorInput = formElement.querySelector('.geofence-color-input') as HTMLInputElement;

    // Update geofence with form values
    const finalGeofence: GeofenceData = {
      ...geofence,
      name: nameInput?.value || geofence.name,
      description: descriptionInput?.value || geofence.description,
      color: colorInput?.value || geofence.color
    };

    // Use the action-based pattern for creation
    const createResult = await this.executeGeofenceCreateAction(finalGeofence);
    
    if (createResult) {
      // Add to internal geofences array at the beginning (so it shows on top) only if action succeeded
      const updatedGeofences = [finalGeofence, ...this.internalGeofences];
      
      // Update the component's internal geofences array
      this.internalGeofences = updatedGeofences;
      
      // Emit the changes for external consumers
      this.geofencesChanged.emit(updatedGeofences);
      this.geofenceCreated.emit(finalGeofence);

      // Add geofence data to the drawn layer
      if (this.currentDrawingGeofence) {
        this.currentDrawingGeofence.geofenceData = finalGeofence;
        this.drawnGeofences.push(this.currentDrawingGeofence);
      }

      // Reset creation state
      this.resetCreationState();
      
      // Restore the full drawer structure and update with new list
      this.restoreFullDrawerStructure();
      
      console.log('Geofence created successfully via saveDrawnGeofence:', finalGeofence);
    } else {
      // Action was cancelled or failed - keep the form open and update editingGeofence with form data
      console.log('Geofence creation was cancelled via saveDrawnGeofence - staying in creation mode');
      
      // Update the current editing geofence with the form values for next attempt
      this.editingGeofence = finalGeofence;
      
      // Update the layer color if it was changed
      if (this.currentDrawingGeofence instanceof L.Path) {
        this.currentDrawingGeofence.setStyle({ 
          color: finalGeofence.color, 
          fillColor: finalGeofence.color 
        });
      }
      
      // Stay in creation mode - don't reset state or close form
      // The user can either try saving again or manually cancel
    }
  }

  private restoreFullDrawerStructure(): void {
    const drawerElement = this.geofenceDrawerContainer.element.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    const drawerBody = drawerElement.querySelector('.h-full.flex.flex-col');
    if (!drawerBody) return;

    // Restore the original structure from the template
    const originalTemplate = this.geofenceDrawerTemplate.nativeElement.cloneNode(true) as HTMLElement;
    const originalBody = originalTemplate.querySelector('.h-full.flex.flex-col');
    
    if (originalBody) {
      drawerBody.innerHTML = originalBody.innerHTML;
      
      // Re-add event listeners to the restored buttons
      this.addGeofenceDrawerEventListeners(drawerElement);
      
      // Update the drawer content with current geofences
      this.updateGeofenceDrawerContent();
    }
  }

  private resetCreationState(): void {
    this.geofenceDrawingMode = false;
    this.currentDrawingTool = null;
    this.creatingGeofenceType = null;
    this.isGeofenceCreateMode = false;
    this.editingGeofence = null;
    this.currentDrawingGeofence = null;
  }

  private updateGeofenceFromLayer(geofence: GeofenceData, layer: L.Layer): void {
    this.mapService.updateGeofenceFromLayer(geofence, layer);
  }

  /**
   * Restores the original geometry of a layer from the stored geofence data.
   * This is used when cancelling edits to restore the original shape and position.
   */
  private restoreLayerGeometry(layer: L.Layer, originalGeofence: GeofenceData): void {
    this.mapService.restoreLayerGeometry(layer, originalGeofence);
  }

  private selectGeofence(geofence: GeofenceData): void {
    // Center the map on the selected geofence
    this.centerMapOnGeofence(geofence);
    
    // Highlight the geofence
    this.highlightGeofence(geofence);
    
    console.log('Geofence selected:', geofence);
  }

  private centerMapOnGeofence(geofence: GeofenceData): void {
    this.mapService.centerMapOnGeofence(this.map, geofence);
  }

  private highlightGeofence(geofence: GeofenceData): void {
    this.mapService.highlightGeofence(geofence, this.drawnGeofences, () => this.clearAllHighlights());
  }

  private clearAllHighlights(): void {
    this.mapService.clearAllHighlights(this.drawnGeofences);
  }

  private clearHighlight(layer: L.Layer): void {
    this.mapService.clearHighlight(layer);
  }

  public deleteGeofence(geofenceId: string): void {
    const geofenceToDelete = this.internalGeofences.find(g => g.id === geofenceId);
    if (!geofenceToDelete) return;

    // Emit deletion event instead of directly modifying
    this.geofenceDeleted.emit(geofenceToDelete);
  }

  /**
   * Execute remove action for a geofence using the configured geofence actions
   */
  private async executeGeofenceRemoveAction(geofence: GeofenceData): Promise<void> {
    // Look for a remove/delete action in the geofenceActions
    const removeAction = this.geofenceActions.find(action => 
      action.key === 'delete' || action.key === 'remove' || 
      action.caption.toLowerCase().includes('delete') || 
      action.caption.toLowerCase().includes('remove') ||
      action.caption.toLowerCase().includes('eliminar')
    );

    if (!removeAction) {
      console.warn('No remove/delete action found in geofenceActions, falling back to direct deletion');
      this.deleteGeofence(geofence.id);
      return;
    }

    if (this.isExecutingAction) {
      console.log('Action already executing, ignoring remove request');
      return;
    }

    try {
      this.isExecutingAction = true;
      console.log(`Executing remove action: ${removeAction.caption} for geofence: ${geofence.name}`);
      
      // Call the handler with the single geofence
      const result = await removeAction.handler([geofence]);
      
      if (result === true) {
        // Action succeeded - remove from internal list and update UI
        console.log('Remove action completed successfully');
        const index = this.internalGeofences.findIndex(g => g.id === geofence.id);
        if (index !== -1) {
          this.internalGeofences.splice(index, 1);
          
          // Also remove from map layers
          const layer: EditableLayer = this.drawnGeofences.find(l => (l as EditableLayer).geofenceData?.id === geofence.id) as EditableLayer;
          if (layer) {
            this.geofenceLayerGroup.removeLayer(layer);
            const layerIndex = this.drawnGeofences.indexOf(layer);
            if (layerIndex !== -1) {
              this.drawnGeofences.splice(layerIndex, 1);
            }
          }
          
          this.updateGeofenceDrawerContent();
          this.geofencesChanged.emit([...this.internalGeofences]);
        }
      } else {
        console.log('Remove action was cancelled or failed');
      }
    } catch (error) {
      console.error('Error executing geofence remove action:', error);
    } finally {
      this.isExecutingAction = false;
    }
  }

  public editGeofence(geofenceId: string): void {
    console.log('Edit geofence called with ID:', geofenceId);
    const geofence = this.internalGeofences.find(g => g.id === geofenceId);
    if (!geofence) {
      console.error('Geofence not found:', geofenceId);
      return;
    }

    console.log('Found geofence:', geofence);

    // Store original state for cancel functionality
    this.originalGeofenceState = JSON.parse(JSON.stringify(geofence));

    // Set edit mode state
    this.editingGeofenceId = geofenceId;
    this.isGeofenceEditMode = true;

    // Center and highlight the geofence
    this.centerMapOnGeofence(geofence);
    this.highlightGeofence(geofence);

    // Make the geofence visually editable on the map
    this.makeGeofenceVisuallyEditable(geofenceId);

    // Update the drawer to show edit mode
    console.log('Showing edit mode for:', geofence.name);
    this.showGeofenceEditMode(geofence);
    
    console.log('Entering edit mode for geofence:', geofence.name);
  }

  private makeGeofenceVisuallyEditable(geofenceId: string): void {
    this.mapService.makeGeofenceVisuallyEditable(
      geofenceId, 
      this.drawnGeofences, 
      this.map,
      this.internalGeofences, 
      (geofence, layer) => this.updateGeofenceFromLayer(geofence, layer)
    );
  }

  private removeEditableVisualStyle(geofenceId: string): void {
    this.mapService.removeEditableVisualStyle(geofenceId, this.drawnGeofences, this.internalGeofences);
  }

  private showGeofenceEditMode(geofence: GeofenceData): void {
    // Call the service method directly with container element since we have ViewContainerRef
    const drawerElement = this.geofenceDrawerContainer.element.nativeElement.querySelector('.geofence-drawer-panel');
    if (!drawerElement) return;
    
    const drawerBody = drawerElement.querySelector('.geo-drawer-content');
    if (!drawerBody) return;

    // Clone the creation form template (same as create mode)
    const formTemplate = this.geofenceCreateFormTemplate.nativeElement.cloneNode(true) as HTMLElement;
    formTemplate.style.display = 'block';
    
    // Set the form title for edit mode
    const titleElement = formTemplate.querySelector('.form-title');
    if (titleElement) {
      titleElement.textContent = `Editar ${this.getGeofenceTypeLabel(geofence.type)}`;
    }

    // Set values from the existing geofence
    const nameInput = formTemplate.querySelector('.geofence-name-input') as HTMLInputElement;
    const descriptionInput = formTemplate.querySelector('.geofence-description-input') as HTMLTextAreaElement;
    const colorInput = formTemplate.querySelector('.geofence-color-input') as HTMLInputElement;
    const colorDisplay = formTemplate.querySelector('.geofence-color-display');

    if (nameInput) {
      nameInput.value = geofence.name;
    }
    if (descriptionInput) {
      descriptionInput.value = geofence.description || '';
    }
    if (colorInput) {
      const color = geofence.color || geofence.style?.color || '#3B82F6';
      colorInput.value = color;
    }
    if (colorDisplay) {
      const color = geofence.color || geofence.style?.color || '#3B82F6';
      colorDisplay.textContent = color;
    }

    // Hide the "Dibujar en Mapa" button since geofence already exists
    const drawBtn = formTemplate.querySelector('.start-drawing-btn') as HTMLElement;
    if (drawBtn) {
      drawBtn.style.display = 'none';
    }

    // Show the save button since we have coordinates
    const saveBtn = formTemplate.querySelector('.save-geofence-btn') as HTMLElement;
    if (saveBtn) {
      saveBtn.style.display = 'inline-block';
    }

    // Add form event listeners for edit mode
    this.addGeofenceFormEventListeners(formTemplate, geofence, true);
    
    // Replace the entire body content with form
    drawerBody.innerHTML = '';
    drawerBody.appendChild(formTemplate);
    
    // Set state
    this.isGeofenceEditMode = true;
    this.editingGeofence = geofence;
  }





  private getUpdatedGeofenceCoordinates(geofenceId: string): Partial<GeofenceData> | null {
    return this.mapService.getUpdatedGeofenceCoordinates(geofenceId, this.drawnGeofences);
  }

  private async saveGeofenceEdits(geofence: GeofenceData): Promise<void> {
    const containerElement = this.geofenceDrawerContainer.element.nativeElement;
    const drawerElement = containerElement.querySelector('.geofence-drawer-panel');
    
    if (!drawerElement) return;
    
    // Use the same class names as the creation form
    const nameInput = drawerElement.querySelector('.geofence-name-input') as HTMLInputElement;
    const colorInput = drawerElement.querySelector('.geofence-color-input') as HTMLInputElement;
    
    if (nameInput && colorInput) {
      // Get updated coordinates from the edited layer
      const updatedCoordinates = this.getUpdatedGeofenceCoordinates(geofence.id);
      
      // Create updated geofence data with potentially new coordinates
      const updatedGeofence: GeofenceData = {
        ...geofence,
        name: nameInput.value,
        color: colorInput.value,
        ...(updatedCoordinates && { ...updatedCoordinates }), // Merge coordinates/center/radius if available
        style: {
          ...geofence.style,
          color: colorInput.value,
          fillColor: colorInput.value
        }
      };
      
      try {
        // Disable buttons during save
        const saveBtn = drawerElement.querySelector('.save-geofence-btn') as HTMLButtonElement;
        const cancelBtn = drawerElement.querySelector('.cancel-geofence-edit-btn') as HTMLButtonElement;
        
        if (saveBtn) {
          saveBtn.disabled = true;
          saveBtn.textContent = 'Saving...';
        }
        if (cancelBtn) cancelBtn.disabled = true;

        // Use geofenceActions edit handler instead of mock save
        const saveResult = await this.executeGeofenceEditAction(updatedGeofence);
        
        if (saveResult) {
          // Update was successful
          console.log('Geofence updated successfully:', updatedGeofence);
          
          // Update the geofence in our local array
          const index = this.internalGeofences.findIndex(g => g.id === geofence.id);
          if (index !== -1) {
            this.internalGeofences[index] = updatedGeofence;
          }
          
          // Emit the changes to parent component
          this.geofencesChanged.emit([...this.internalGeofences]);
          
          // Update the layer data
          const layer: EditableLayer = this.drawnGeofences.find(l => 
            (l as EditableLayer).geofenceData?.id === geofence.id
          ) as EditableLayer;
          if (layer) {
            layer.geofenceData = updatedGeofence;
            
            // Update layer style with new color
            if (layer.setStyle) {
              layer.setStyle({
                color: updatedGeofence.color,
                fillColor: updatedGeofence.color,
                weight: updatedGeofence.style?.weight || 2,
                fillOpacity: updatedGeofence.style?.fillOpacity || 0.3
              });
            }
          }

          // Emit the update event
          this.geofenceUpdated.emit(updatedGeofence);
          
          // Calculate and emit marker information
          this.calculateAndEmitGeofenceMarkers(updatedGeofence);
          
          // Exit edit mode (this will recreate the drawer with updated content)
          console.log('About to call exitGeofenceEditMode...');
          this.exitGeofenceEditMode();
          console.log('exitGeofenceEditMode called successfully');
        } else {
          // Save failed, re-enable buttons
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = this.captions.saveGeofenceButton || 'Save';
          }
          if (cancelBtn) cancelBtn.disabled = false;
          
          console.log('Geofence edit was cancelled or failed');
          // Don't exit edit mode, let user try again or cancel manually
        }
      } catch (error) {
        console.error('Error saving geofence:', error);
        // Re-enable buttons on error
        const saveBtn = drawerElement.querySelector('.save-geofence-btn') as HTMLButtonElement;
        const cancelBtn = drawerElement.querySelector('.cancel-geofence-edit-btn') as HTMLButtonElement;
        
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.textContent = this.captions.saveGeofenceButton || 'Save';
        }
        if (cancelBtn) cancelBtn.disabled = false;
      }
    }
  }

  private calculateAndEmitGeofenceMarkers(geofence: GeofenceData): void {
    // Get all markers from all groups
    const allMarkers: { latitude: number; longitude: number }[] = [];
    const markerToGroupMap = new Map<any, { marker: MarkerData, group: ProcessedMarkerGroup }>();
    
    this.processedGroups.forEach(group => {
      group.markers.forEach(marker => {
        const markerPoint = { latitude: marker.latitude, longitude: marker.longitude };
        allMarkers.push(markerPoint);
        markerToGroupMap.set(markerPoint, { marker, group });
      });
    });

    // Get markers inside this geofence
    const markersInside = this.geoService.getMarkersInGeofence(geofence, allMarkers);
    
    // Convert to SelectedMarker format
    const selectedMarkers: SelectedMarker[] = markersInside.map(marker => {
      const markerInfo = markerToGroupMap.get(marker);
      return markerInfo!;
    }).filter(marker => marker !== undefined);

    // Create the event data
    const eventData: GeofenceMarkersEvent = {
      geofence: geofence,
      markers: selectedMarkers,
      totalCount: selectedMarkers.length
    };

    console.log(`📊 Geofence "${geofence.name}" contains ${selectedMarkers.length} markers:`, selectedMarkers);
    
    // Emit the event
    this.geofenceMarkersCalculated.emit(eventData);
  }

  private cancelGeofenceEdit(): void {
    console.log('Canceling geofence edit');
    
    // Restore original geofence state
    if (this.originalGeofenceState && this.editingGeofenceId) {
      const index = this.internalGeofences.findIndex(g => g.id === this.editingGeofenceId);
      if (index !== -1) {
        this.internalGeofences[index] = { ...this.originalGeofenceState };
        
        // Update the layer with original data and restore geometry
        const layer: EditableLayer = this.drawnGeofences.find(l => 
          (l as EditableLayer).geofenceData?.id === this.editingGeofenceId
        );
        if (layer) {
          layer.geofenceData = this.originalGeofenceState;
          
          // Restore original geometry (shape and position)
          this.restoreLayerGeometry(layer, this.originalGeofenceState);
          
          // Restore original visual style
          if (layer.setStyle) {
            layer.setStyle({
              color: this.originalGeofenceState.color || this.originalGeofenceState.style?.color || '#3B82F6',
              fillColor: this.originalGeofenceState.fillColor || this.originalGeofenceState.style?.fillColor || '#3B82F650',
              weight: this.originalGeofenceState.style?.weight || 2,
              fillOpacity: this.originalGeofenceState.style?.fillOpacity || 0.3,
              opacity: 1,
              dashArray: null
            });
          }
        }
      }
    }
    
    // Exit edit mode without saving
    this.exitGeofenceEditMode();
  }

  private exitGeofenceEditMode(): void {
    console.log('exitGeofenceEditMode - START');
    
    // Clear any highlights first
    this.clearAllHighlights();
    
    // Remove visual editing style
    if (this.editingGeofenceId) {
      console.log('Removing editable visual style for:', this.editingGeofenceId);
      this.removeEditableVisualStyle(this.editingGeofenceId);
    }
    
    // Reset edit mode state (same pattern as create mode)
    this.isGeofenceEditMode = false;
    this.editingGeofenceId = null;
    this.editingGeofence = null;
    this.originalGeofenceState = null;
    
    // Restore full drawer structure (same as create m
    // .+ode does)
    this.restoreFullDrawerStructure();
    
    console.log('exitGeofenceEditMode - END');
  }

  private updateGeofenceColor(geofenceId: string, newColor: string): void {
    this.mapService.updateGeofenceColor(geofenceId, newColor, this.drawnGeofences);
  }

  public clearAllGeofences(): void {
    this.internalGeofences = [];
    this.geofenceLayerGroup.clearLayers();
    this.drawnGeofences = [];
    this.updateGeofenceDrawerContent();
    this.geofencesChanged.emit([...this.internalGeofences]);
  }

  // Public methods for external control
  public getSelectedMarkers(): SelectedMarker[] {
    return [...this.selectedMarkers];
  }

  public clearSelections(): void {
    this.clearAllSelections();
  }

  public getSelectionModeState(): boolean {
    return this.isSelectionModeActive;
  }

  // Debug method - can be called from browser console for troubleshooting
  public debugDrawerState(): void {
    console.log('=== Drawer Debug Info ===');
    console.log('Component ref exists:', !!this.drawerComponentRef);
    console.log('Is animating:', this.isAnimating);
    console.log('Container element count:', this.drawerContainer?.length || 'N/A');
    if (this.drawerComponentRef) {
      console.log('Current marker:', this.drawerComponentRef.instance.marker?.title);
      console.log('Is open:', this.drawerComponentRef.instance.isOpen);
    }
    
    // Check actual DOM elements
    const containerElement = this.drawerContainer?.element?.nativeElement;
    if (containerElement) {
      console.log('DOM children count:', containerElement.children.length);
      console.log('DOM content:', containerElement.innerHTML);
      
      // Check for orphaned drawers in parent
      const parentElement = containerElement.parentElement;
      if (parentElement) {
        const orphanedDrawers = parentElement.querySelectorAll('.drawer-panel, app-marker-drawer');
        console.log('Orphaned drawer elements found:', orphanedDrawers.length);
        orphanedDrawers.forEach((el, i) => console.log(`Orphan ${i + 1}:`, el));
      }
    }
    console.log('========================');
  }

  // Manual cleanup method for debugging
  public forceCleanupDrawers(): void {
    console.log('Manual force cleanup initiated');
    this.destroyDrawer();
    this.geoService.cleanupContainer(this.drawerContainer);
    console.log('Manual force cleanup completed');
  }

  closeDrawer(onComplete?: () => void): void {
    console.log('Close drawer called');
    
    if (!this.drawerComponentRef) {
      console.log('No drawer to close');
      if (onComplete) onComplete();
      return;
    }
    
    if (this.isAnimating) {
      console.log('Already animating, waiting for completion before closing');
      // Wait a bit and try again
      setTimeout(() => {
        this.closeDrawer(onComplete);
      }, 100);
      return;
    }
    
    this.isAnimating = true;
    
    // Use service to animate and destroy
    this.geoService.animateDrawerClose(this.drawerComponentRef).then(() => {
      this.drawerComponentRef = null;
      this.isAnimating = false;
      console.log('Drawer component destroyed');
      
      if (onComplete) {
        onComplete();
      }
    });
  }

  private destroyDrawer(): void {
    if (this.drawerComponentRef) {
      console.log('Destroying drawer component');
      
      // Store reference for logging
      const componentInstance = this.drawerComponentRef.instance;
      
      // Use service to destroy component
      this.geoService.destroyDrawerComponent(this.drawerComponentRef);
      this.drawerComponentRef = null;
      
      // Clear the container to ensure clean state
      this.geoService.cleanupContainer(this.drawerContainer);
      
      console.log('Drawer component destroyed for marker:', componentInstance?.marker?.title);
    } else {
      // Even if no component ref, ensure container is clean
      this.geoService.cleanupContainer(this.drawerContainer);
    }
  }

}
